import type { FamilyScopeRequirementAssessmentInput } from '../api/grpc/familyRequirementAssessmentClient';
import type {
  AdditionalCategoriesConfiguration,
  BoltConfiguration,
  ConnectorBindingConfiguration,
  MenuConfiguration,
} from '../types';
import { buildAdditionalCategoryAdditionOperations } from './hooks/parameterAddition/additionalCategoryPlan';
import { buildConnectorAdditionOperations } from './hooks/parameterAddition/connectorPlan';
import type {
  ParameterBindingKind,
  ParameterTypeHint,
} from './revit/projectParameterMutationPlan';
import { buildTextParameterTypeHints } from './revit/wizardParameterDataTypes';
import {
  getEquivalentRevitParameterNames,
  sanitizeRevitParameterNames,
} from './revitParameterNames';
import {
  extractBoltParameterNames,
  getTargetObjectType,
  normalizeBoltProjectParameterCategories,
  resolveBoltParameterTypeHints,
} from '../services/boltParameterSetup';
import { toBoltConfigurationInput } from './utils/boltConfigurationInput';
import { workflowIncludesIsoOnlySetup } from '../config/workflowUi';

export type MappingParameterDestination = 'project' | 'family';
export type MappingParameterScopeArea =
  | 'connector'
  | 'bolt'
  | 'additional'
  | 'process'
  | 'project'
  | 'mapping';

export type MappingParameterRepairKind =
  | 'connector'
  | 'processProject'
  | 'additional'
  | 'bolt'
  | 'mapping';

export type FamilyEmptyMatchPolicy = 'required' | 'non-applicable';

export type MappingParameterRequirementTarget =
  | {
      kind: 'projectCategories';
      categories: string[];
    }
  | {
      kind: 'editableFamilyCategories';
      categories: string[];
      emptyMatchPolicy: FamilyEmptyMatchPolicy;
    }
  | {
      kind: 'familyObjectTypes';
      objectTypes: string[];
      discriminatorParameterName: string;
      // The discriminator's own binding route comes from the page that owns the
      // classification route (the connector page for bolt requirements, the
      // additional-categories page for additional-category requirements), not
      // from the requirement's bindingKind: parameters can be family-type bound
      // while the object-type classification is written as a family-instance
      // value. Snapshot intents routed with the wrong binding kind hide the
      // classification evidence from readiness matching.
      discriminatorDestination: MappingParameterDestination;
      discriminatorBindingKind: ParameterBindingKind;
      emptyMatchPolicy: FamilyEmptyMatchPolicy;
    };

export interface MappingParameterScopeRequirement {
  key: string;
  parameterName: string;
  area: MappingParameterScopeArea;
  repairKind: MappingParameterRepairKind;
  destination: MappingParameterDestination;
  bindingKind: ParameterBindingKind;
  target: MappingParameterRequirementTarget;
  parameterTypeHints: ParameterTypeHint[];
}

export type MappingParameterRequirement = MappingParameterScopeRequirement;

export interface MappingRepairProjectBinding {
  parameterName: string;
  bindingKind: ParameterBindingKind;
}

export interface WizardSetupScopeRequirementsInput {
  menuConfig: MenuConfiguration;
  connectorConfig: ConnectorBindingConfiguration;
  boltConfig: BoltConfiguration;
  additionalCategoriesConfig: AdditionalCategoriesConfiguration;
}

function normalizePart(value: string): string {
  return value.trim().toLowerCase();
}

function normalizeList(values: string[]): string[] {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)))
    .sort((left, right) => left.localeCompare(right));
}

function targetKey(target: MappingParameterRequirementTarget): string {
  if (target.kind === 'projectCategories') {
    return `${target.kind}:${normalizeList(target.categories).map(normalizePart).join(',')}`;
  }

  if (target.kind === 'editableFamilyCategories') {
    return [
      target.kind,
      target.emptyMatchPolicy,
      normalizeList(target.categories).map(normalizePart).join(','),
    ].join(':');
  }

  return [
    target.kind,
    target.emptyMatchPolicy,
    normalizePart(target.discriminatorParameterName),
    target.discriminatorDestination,
    target.discriminatorBindingKind,
    normalizeList(target.objectTypes).map(normalizePart).join(','),
  ].join(':');
}

export function buildMappingParameterScopeRequirementKey(input: {
  parameterName: string;
  area: MappingParameterScopeArea;
  destination: MappingParameterDestination;
  bindingKind: ParameterBindingKind;
  target: MappingParameterRequirementTarget;
}): string {
  return [
    input.area,
    input.destination,
    input.bindingKind,
    targetKey(input.target),
    normalizePart(input.parameterName),
  ].join(':');
}

export function appendMappingParameterScopeRequirements(
  target: MappingParameterScopeRequirement[],
  input: {
    parameterNames: string[];
    area: MappingParameterScopeArea;
    repairKind: MappingParameterRepairKind;
    destination: MappingParameterDestination;
    bindingKind: ParameterBindingKind;
    target: MappingParameterRequirementTarget;
    parameterTypeHints?: ParameterTypeHint[];
  }
): void {
  const hints = input.parameterTypeHints ?? [];
  sanitizeRevitParameterNames(input.parameterNames).forEach((parameterName) => {
    const equivalentNames = new Set(
      getEquivalentRevitParameterNames(parameterName).map(normalizePart)
    );
    const parameterTypeHints = hints.filter((hint) =>
      equivalentNames.has(normalizePart(hint.name))
    );
    target.push({
      key: buildMappingParameterScopeRequirementKey({
        parameterName,
        area: input.area,
        destination: input.destination,
        bindingKind: input.bindingKind,
        target: input.target,
      }),
      parameterName,
      area: input.area,
      repairKind: input.repairKind,
      destination: input.destination,
      bindingKind: input.bindingKind,
      target: input.target,
      parameterTypeHints,
    });
  });
}

export function buildConnectorParameterScopeRequirements(input: {
  menuConfig: MenuConfiguration;
  connectorConfig: ConnectorBindingConfiguration;
}): MappingParameterScopeRequirement[] {
  const requirements: MappingParameterScopeRequirement[] = [];
  buildConnectorAdditionOperations({
    menuConfig: input.menuConfig,
    connectorConfig: input.connectorConfig,
  }).forEach((operation) => {
    appendMappingParameterScopeRequirements(requirements, {
      parameterNames: operation.parameterNames,
      area: 'connector',
      repairKind: 'connector',
      destination: operation.destination,
      bindingKind: operation.bindingKind,
      target: operation.destination === 'project'
        ? { kind: 'projectCategories', categories: operation.categories }
        : {
            kind: 'editableFamilyCategories',
            categories: operation.categories,
            emptyMatchPolicy: 'non-applicable',
          },
      parameterTypeHints: operation.parameterTypeHints,
    });
  });
  return requirements;
}

export function buildAdditionalCategoryParameterScopeRequirements(input: {
  additionalCategoriesConfig: AdditionalCategoriesConfiguration;
  objectTypeParameterName: string;
}): MappingParameterScopeRequirement[] {
  const requirements: MappingParameterScopeRequirement[] = [];
  buildAdditionalCategoryAdditionOperations(input.additionalCategoriesConfig).forEach(
    (operation) => {
      const names = operation.parameterNames;
      appendMappingParameterScopeRequirements(requirements, {
        parameterNames: names,
        area: 'additional',
        repairKind: 'additional',
        destination: operation.destination,
        bindingKind: operation.bindingKind,
        target: operation.destination === 'project'
          ? { kind: 'projectCategories', categories: operation.categories ?? [] }
          : {
              kind: 'familyObjectTypes',
              objectTypes: operation.targetObjectTypes ?? [],
              discriminatorParameterName: input.objectTypeParameterName.trim(),
              // Additional-category classification is written on the route the
              // user selected on the additional-categories page; the connector
              // page's mode describes a different page's write.
              discriminatorDestination:
                input.additionalCategoriesConfig.parameterDestination === 'project'
                  ? 'project'
                  : 'family',
              discriminatorBindingKind:
                input.additionalCategoriesConfig.parameterBindingKind === 'type'
                  ? 'type'
                  : 'instance',
              emptyMatchPolicy: 'non-applicable',
            },
        parameterTypeHints: buildTextParameterTypeHints(names),
      });
    }
  );
  return requirements;
}

export function buildWizardSetupParameterScopeRequirements(
  input: WizardSetupScopeRequirementsInput
): MappingParameterScopeRequirement[] {
  const requirements = buildConnectorParameterScopeRequirements(input);

  if (workflowIncludesIsoOnlySetup(input.menuConfig.selectedOption)) {
    const boltInput = toBoltConfigurationInput(input.boltConfig, {
      parameterName: input.menuConfig.objectTypeValue,
      bindingKind: input.connectorConfig.parameterBindingKind,
    });
    const boltDestination = boltInput.parameterDestination === 'project' ? 'project' : 'family';
    const boltNames = extractBoltParameterNames(boltInput);
    appendMappingParameterScopeRequirements(requirements, {
      parameterNames: boltNames,
      area: 'bolt',
      repairKind: 'bolt',
      destination: boltDestination,
      bindingKind: boltInput.parameterBindingKind === 'instance' ? 'instance' : 'type',
      target: boltDestination === 'project'
        ? {
            kind: 'projectCategories',
            categories: normalizeBoltProjectParameterCategories(
              boltInput.projectParameterCategories
            ),
          }
        : {
            kind: 'familyObjectTypes',
            objectTypes: [getTargetObjectType(boltInput.boltModelingChoice)],
            discriminatorParameterName: boltInput.objectTypeParameterName,
            discriminatorDestination: input.connectorConfig.parameterDestination,
            discriminatorBindingKind: input.connectorConfig.parameterBindingKind,
            emptyMatchPolicy: 'required',
          },
      parameterTypeHints: resolveBoltParameterTypeHints(boltInput),
    });
  }

  if (workflowIncludesIsoOnlySetup(input.menuConfig.selectedOption)) {
    requirements.push(
      ...buildAdditionalCategoryParameterScopeRequirements({
        additionalCategoriesConfig: input.additionalCategoriesConfig,
        objectTypeParameterName: input.menuConfig.objectTypeValue?.trim() ?? '',
      })
    );
  }

  return requirements;
}

export function selectRepairProjectBindings(
  requirements: MappingParameterScopeRequirement[]
): MappingRepairProjectBinding[] {
  const projectBindings = requirements
    .filter((requirement) =>
      requirement.destination === 'project' && requirement.target.kind === 'projectCategories'
    )
    .map(({ parameterName, bindingKind }) => ({ parameterName, bindingKind }));
  return Array.from(new Map(
    projectBindings.map((binding) => [
      `${normalizePart(binding.parameterName)}:${binding.bindingKind}`,
      binding,
    ])
  ).values());
}

function requirementDataTypeHint(requirement: MappingParameterScopeRequirement): string {
  const names = new Set(
    getEquivalentRevitParameterNames(requirement.parameterName).map((name) =>
      name.trim().toLowerCase()
    )
  );
  return (
    requirement.parameterTypeHints.find((hint) =>
      names.has(hint.name.trim().toLowerCase())
    )?.dataType ?? ''
  );
}

/**
 * Serializes the family-scope requirements for the backend assessment rpc.
 * projectCategories targets stay project-catalog-assessed on the FE, so they
 * are never sent; only editableFamilyCategories and familyObjectTypes targets
 * cross the wire.
 */
export function buildMappingFamilyRequirementAssessmentInputs(
  requirements: MappingParameterScopeRequirement[]
): FamilyScopeRequirementAssessmentInput[] {
  return requirements
    .filter((requirement) => requirement.target.kind !== 'projectCategories')
    .map((requirement) => {
      const target = requirement.target;
      const objectTypeTarget = target.kind === 'familyObjectTypes' ? target : null;
      return {
        key: requirement.key,
        parameterName: requirement.parameterName,
        destination: requirement.destination,
        bindingKind: requirement.bindingKind,
        targetKind: target.kind as FamilyScopeRequirementAssessmentInput['targetKind'],
        categories: target.kind === 'editableFamilyCategories' ? [...target.categories] : [],
        objectTypes: objectTypeTarget ? [...objectTypeTarget.objectTypes] : [],
        discriminatorParameterName: objectTypeTarget?.discriminatorParameterName ?? '',
        discriminatorDestination: objectTypeTarget?.discriminatorDestination ?? '',
        discriminatorBindingKind: objectTypeTarget?.discriminatorBindingKind ?? '',
        emptyMatchPolicy: target.kind === 'projectCategories' ? '' : target.emptyMatchPolicy,
        dataTypeHint: requirementDataTypeHint(requirement),
      };
    });
}
